import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Download, MapPin } from "lucide-react";
import { HOTSPOTS } from "@/lib/site";
import { EASE, Reveal, SectionHead } from "./shared";

const LakeMap = ({ active, onSelect }) => (
  <div className="relative w-full overflow-hidden rounded-2xl border border-gold/30 shadow-2xl" data-testid="lake-map">
    <svg viewBox="0 0 800 600" className="block h-auto w-full" role="img" aria-label="Mappa illustrata del Lago d'Orta">
      <defs>
        <radialGradient id="lakeGrad" cx="48%" cy="45%" r="65%">
          <stop offset="0%" stopColor="#2C6B7A" />
          <stop offset="70%" stopColor="#133E4A" />
          <stop offset="100%" stopColor="#0C2830" />
        </radialGradient>
      </defs>
      <rect width="800" height="600" fill="#EFE7D5" />
      <ellipse cx="400" cy="300" rx="330" ry="260" fill="none" stroke="#C59B27" strokeOpacity="0.22" />
      <ellipse cx="400" cy="300" rx="270" ry="205" fill="none" stroke="#C59B27" strokeOpacity="0.16" />
      <ellipse cx="400" cy="300" rx="205" ry="150" fill="none" stroke="#C59B27" strokeOpacity="0.12" />
      <path d="M40 120 L120 40 L200 130 Z" fill="#133E4A" opacity="0.12" />
      <path d="M600 560 L690 470 L770 570 Z" fill="#133E4A" opacity="0.12" />
      <path d="M620 60 L700 10 L760 90 Z" fill="#133E4A" opacity="0.1" />
      <path
        d="M412,38 C368,78 338,146 348,222 C356,290 318,352 300,420 C282,486 332,556 402,564 C472,572 522,516 514,438 C507,368 482,300 472,232 C461,152 458,66 412,38 Z"
        fill="url(#lakeGrad)"
        stroke="#C59B27"
        strokeOpacity="0.55"
        strokeWidth="1.5"
      />
      <path
        d="M412,60 C450,140 470,300 470,360 C470,470 440,540 404,548"
        fill="none"
        stroke="#E5C365"
        strokeOpacity="0.4"
        strokeWidth="1"
        strokeDasharray="3 6"
      />
      <ellipse cx="400" cy="312" rx="30" ry="21" fill="#DFD3BD" stroke="#C59B27" strokeWidth="1.5" />
      <rect x="394" y="292" width="9" height="16" fill="#133E4A" />
      <path d="M393 292 L398.5 284 L404 292 Z" fill="#C59B27" />
      <circle cx="598" cy="240" r="7" fill="none" stroke="#5A5248" strokeOpacity="0.5" />
      <circle cx="540" cy="360" r="5" fill="none" stroke="#5A5248" strokeOpacity="0.5" />
      <g transform="translate(712,92)">
        <circle r="26" fill="none" stroke="#C59B27" strokeOpacity="0.6" />
        <path d="M0 -18 L5 6 L0 2 L-5 6 Z" fill="#C59B27" />
        <text y="-32" textAnchor="middle" fill="#5A5248" fontFamily="serif" fontSize="13">N</text>
      </g>
      <text x="400" y="590" textAnchor="middle" fill="#5A5248" fontSize="17" letterSpacing="8" fontFamily="'Cinzel Decorative', serif">
        LAGO D'ORTA
      </text>
      <text x="400" y="345" textAnchor="middle" fill="#F8F4EB" fontSize="11" fontStyle="italic" fontFamily="serif" opacity="0.85">
        Cusius
      </text>
    </svg>

    {HOTSPOTS.map((h) => (
      <button
        key={h.id}
        data-testid={`map-hotspot-${h.id}`}
        onClick={() => onSelect(h.id)}
        aria-label={h.name}
        className="group absolute -translate-x-1/2 -translate-y-1/2"
        style={{ left: `${h.x}%`, top: `${h.y}%` }}
      >
        <span
          className={`absolute inset-0 -m-2 animate-ping rounded-full ${
            active === h.id ? "bg-gold/40" : "bg-gold/20"
          }`}
          style={{ animationDuration: "2.6s" }}
        />
        <span
          className={`relative flex h-9 w-9 items-center justify-center rounded-full border backdrop-blur-sm transition-all duration-300 ${
            active === h.id
              ? "border-gold-shimmer bg-gold text-ink-dark shadow-[0_0_20px_rgba(197,155,39,0.6)]"
              : "border-gold/60 bg-lake-deep/70 text-gold-shimmer group-hover:border-gold-shimmer group-hover:bg-gold/20"
          }`}
        >
          <MapPin className="h-4 w-4" />
        </span>
      </button>
    ))}
  </div>
);

export default function Mondo() {
  const [active, setActive] = useState("san-giulio");
  const spot = HOTSPOTS.find((h) => h.id === active);

  return (
    <section id="mondo" className="relative overflow-hidden py-20 lg:py-32">
      <img
        src="/assets/lago-riva.jpg"
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-20"
      />
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-lake-deep via-lake-teal/80 to-lake-deep" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHead
          kicker="Il Mondo"
          title="I luoghi del romanzo esistono davvero"
          sub="Ogni pagina de Il Segreto del Lago è camminabile: esplora la mappa e scopri come le leggende vere del Lago d'Orta si intrecciano con la storia."
        />

        <div className="mt-14 grid grid-cols-1 gap-8 lg:grid-cols-12">
          <Reveal className="lg:col-span-8">
            <LakeMap active={active} onSelect={setActive} />
          </Reveal>

          <div className="lg:col-span-4">
            <AnimatePresence mode="wait">
              <motion.div
                key={spot.id}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.5, ease: EASE }}
                className="card-parchment overflow-hidden rounded-xl"
                data-testid="map-info-panel"
              >
                {spot.fotos &&
                  (spot.fotos.length === 1 ? (
                    <img
                      src={spot.fotos[0]}
                      alt={`Fotografia: ${spot.name}`}
                      className="h-44 w-full object-cover"
                      style={spot.fotoPos ? { objectPosition: spot.fotoPos } : undefined}
                      data-testid="map-info-photo"
                    />
                  ) : (
                    <div className="grid grid-cols-2 gap-0.5 bg-parchment-aged" data-testid="map-info-photo">
                      {spot.fotos.map((f, i) => (
                        <img
                          key={i}
                          src={f}
                          alt={`Fotografia: ${spot.name}`}
                          className={`${spot.fotos.length > 2 ? "h-28" : "h-36"} w-full object-cover`}
                          loading="lazy"
                        />
                      ))}
                    </div>
                  ))}
                <div className="p-6 sm:p-8">
                <p className="text-[11px] uppercase tracking-[0.25em] text-gold">Luogo del romanzo</p>
                <h3 className="mt-3 font-serif text-2xl text-ink-dark" data-testid="map-info-title">
                  {spot.name}
                </h3>
                <p className="mt-4 text-sm leading-relaxed text-ink-muted">{spot.desc}</p>
                <div className="gold-line my-5 w-full opacity-70" />
                <p className="font-serif text-base italic leading-relaxed text-lake-teal" data-testid="map-info-lore">
                  {spot.lore}
                </p>
                {spot.fotos && (
                  <p className="mt-4 text-[10px] uppercase tracking-[0.2em] text-ink-muted/70">
                    {spot.fotoCredit || "Foto di Patrizia Franzina"}
                  </p>
                )}
                </div>
              </motion.div>
            </AnimatePresence>

            <div className="mt-6 flex flex-wrap gap-2">
              {HOTSPOTS.map((h) => (
                <button
                  key={h.id}
                  data-testid={`map-selector-${h.id}`}
                  onClick={() => setActive(h.id)}
                  className={`rounded-full border px-4 py-2 text-xs font-medium transition-colors duration-300 ${
                    active === h.id
                      ? "border-gold bg-gold/15 text-gold-shimmer"
                      : "border-parchment-ivory/20 text-parchment-ivory/60 hover:border-gold/50 hover:text-parchment-ivory"
                  }`}
                >
                  {h.name}
                </button>
              ))}
            </div>

            <Reveal delay={0.2}>
              <div className="mt-8 rounded-xl border border-gold/30 bg-lake-deep/60 p-6 backdrop-blur-sm" data-testid="itinerary-card">
                <p className="text-[11px] uppercase tracking-[0.25em] text-gold">Da proporre al territorio</p>
                <h3 className="mt-2 font-serif text-xl text-parchment-ivory">L'Itinerario del Romanzo</h3>
                <p className="mt-3 text-sm leading-relaxed text-parchment-ivory/65">
                  Otto tappe reali — da Omegna a Pettenasco, con il battello per l'isola — in un
                  percorso turistico-letterario scaricabile, pensato per pro loco, biblioteche,
                  librerie ed enti turistici del Lago d'Orta.
                </p>
                <a
                  data-testid="map-itinerary-download"
                  href="/assets/itinerario-il-segreto-del-lago.pdf"
                  download
                  className="mt-5 inline-flex items-center gap-2 rounded-full bg-gold px-6 py-3 text-sm font-semibold text-ink-dark transition-transform duration-300 hover:-translate-y-0.5"
                >
                  <Download className="h-4 w-4" />
                  Scarica l'itinerario (PDF)
                </a>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
